import { State } from '../StateTypes';
import {
  selectSaveVpcProgressStatus,
  selectSaveVpcState,
} from '../vpc/vpcSelectors';
import { VpcSaveProgressStatusEnum } from '../vpc/vpcTypes';
import { ConfirmationSummaryShareDesignStateEnum } from '@inter-ikea-kompis/component-configuration-summary';
import { AddToBagStateEnum } from '@inter-ikea-kompis/component-add-to-bag';

export const selectSummary = (state: State) => state.summary;

export const selectLocale = (state: State) => selectSummary(state).locale;

export const selectStoreId = (state: State) => selectSummary(state).storeId;

export const selectShoppingItems = (state: State) =>
  selectSummary(state).shoppingItems;

export const selectStoreAvailabilities = (state: State) =>
  selectSummary(state).storeAvailabilities;

export const selectDesignLink = (state: State) =>
  selectSummary(state).designLink;

export const selectConfirmationCardType = (state: State) =>
  selectSummary(state).confirmationCardType;

export const selectSceneImage = (state: State) =>
  selectSummary(state).sceneImage;

export const selectModalState = (state: State) =>
  selectSummary(state).modal.showModal;

export const selectAddToCartState = (state: State) =>
  selectSummary(state).states.addToCart;

export const selectModalType = (state: State) =>
  selectSummary(state).modal.modalType;

export const selectAddUnavailableProductsToList = (state: State) =>
  selectSummary(state).addUnavailableProductsToList;

export const selectFailedShoppingItems = (state: State) =>
  selectSummary(state).failedShoppingItems;

export const selectAddToListState = (state: State) =>
  selectSummary(state).states.addToList;

export const selectCurrentConfiguration = (state: State) =>
  selectSummary(state).currentConfiguration;

export const selectShareDesignCard = (state: State) =>
  selectSummary(state).shareDesignCard;

export const selectHomeDelivery = (state: State) =>
  selectSummary(state).homeDelivery;

export const selectFinancingOption = (state: State) =>
  selectSummary(state).financingOptions;

/**
 * Selector returning true if the design link is either ready, or,
 * alternatively, its generation has failed (which can happen if
 * VPC save fails). Returns false if its generation has not yet
 * started or is still in progress.
 * @param state
 * @returns {boolean} The current status of the design link.
 */
export const selectIsDesignLinkEitherReadyOrFailed = (
  state: State
): boolean => {
  const vpcSuccess: boolean =
    selectSaveVpcProgressStatus(state) === VpcSaveProgressStatusEnum.success;
  const vpcFailure: boolean =
    selectSaveVpcProgressStatus(state) === VpcSaveProgressStatusEnum.failure;
  const designLinkPresent: boolean = !!selectDesignLink(state);

  return vpcFailure || (vpcSuccess && designLinkPresent);
};

/**
 * Checks whether any "user action" is in progress, i.e., whether either "Add to cart",
 * "Add to list" or "Save VPC" is in loading state.
 * @param state
 * @returns {boolean} Whether there is any "user action" in progress, as defined above.
 */
export const selectIsAnyUserActionInProgress = (state: State): boolean => {
  const saveVpcState: ConfirmationSummaryShareDesignStateEnum =
    selectSaveVpcState(state);
  const addToCartState: AddToBagStateEnum = selectAddToCartState(state);
  const addToListState: AddToBagStateEnum = selectAddToListState(state);

  const saveVpcLoading: boolean =
    saveVpcState === ConfirmationSummaryShareDesignStateEnum.loading;
  const addToCartLoading: boolean =
    addToCartState === AddToBagStateEnum.loading;
  const addToListLoading: boolean =
    addToListState === AddToBagStateEnum.loading;

  return saveVpcLoading || addToCartLoading || addToListLoading;
};
